import { type IAdditionalField, type IDBOptionAdditionalField } from '../../License/types/request';
import { DataType, Tab } from '../../License/types/enums';

import {
  ArrayFields,
  ConsignmentTabFields,
  HeaderTabFields,
  NamesAndPartiesTabFields,
} from '../../License/types/fieldEnums';
import { type GroupedAdditionalFieldConfigType } from '../../License/types/response';
import { type IDifference } from '../../../store/audit/types';
import { LicenseTypeNature } from '../../../types';

import { formatDate } from '../../../helpers/dateHelpers';

export function getDateFromTimestamp(timeStamp: string) {
  return formatDate(Number(timeStamp), 'dd/MM/yyyy HH:mm:ss');
}

export function getSelectValueFromTimeStamp(timeStamp: string) {
  return {
    label: getDateFromTimestamp(timeStamp),
    value: timeStamp,
  };
}

export function getAdditionalFieldIndexesById(additionalFields: (IAdditionalField | IDBOptionAdditionalField)[]) {
  return additionalFields.reduce((acc, cv, currentIndex) => {
    const { fieldId } = cv;
    acc[fieldId] = currentIndex;

    return acc;
  }, {} as Record<number, number>);
}

export function getTabChanges({
  differences,
  additionalFieldsByTab,
  additionalFields,
  dbOptionAdditionalFields,
  licenseTypeNature,
}: {
  differences: IDifference[];
  additionalFieldsByTab: GroupedAdditionalFieldConfigType;
  additionalFields: IAdditionalField[];
  dbOptionAdditionalFields: IDBOptionAdditionalField[];
  licenseTypeNature?: LicenseTypeNature;
}) {
  const additionalFieldIndexesById = getAdditionalFieldIndexesById(additionalFields);
  const dbOptionAdditionalFieldIndexesById = getAdditionalFieldIndexesById(dbOptionAdditionalFields);

  const headerTabIndexes = additionalFieldsByTab[Tab.HEADER]?.flat().reduce(
    (acc, currentField) => {
      if (currentField) {
        const { id, dataType } = currentField;

        if (dataType === DataType.OPTIONS_FROM_DATABASE) {
          acc.dbOptionAdditionalFields.push(dbOptionAdditionalFieldIndexesById[id] as number);
        } else {
          acc.additionalFields.push(additionalFieldIndexesById[id] as number);
        }
      }

      return acc;
    },
    {
      additionalFields: [] as number[],
      dbOptionAdditionalFields: [] as number[],
    }
  );

  const namesAndPartiesTabIndexes = additionalFieldsByTab[Tab.NAMES_AND_PARTIES]?.flat().reduce(
    (acc, currentField) => {
      if (currentField) {
        const { id, dataType } = currentField;

        if (dataType === DataType.OPTIONS_FROM_DATABASE) {
          acc.dbOptionAdditionalFields.push(dbOptionAdditionalFieldIndexesById[id] as number);
        } else {
          acc.additionalFields.push(additionalFieldIndexesById[id] as number);
        }
      }

      return acc;
    },
    {
      additionalFields: [] as number[],
      dbOptionAdditionalFields: [] as number[],
    }
  );

  const consignmentTabIndexes = additionalFieldsByTab[Tab.CONSIGNMENT]?.flat().reduce(
    (acc, currentField) => {
      if (currentField) {
        const { id, dataType } = currentField;

        if (dataType === DataType.OPTIONS_FROM_DATABASE) {
          acc.dbOptionAdditionalFields.push(dbOptionAdditionalFieldIndexesById[id] as number);
        } else {
          acc.additionalFields.push(additionalFieldIndexesById[id] as number);
        }
      }

      return acc;
    },
    {
      additionalFields: [] as number[],
      dbOptionAdditionalFields: [] as number[],
    }
  );

  return {
    header: differences.some((difference) => {
      const [, fieldName, index] = difference.path.split('/') as [string, string, string];

      if (fieldName === 'additionalFields') {
        return !!headerTabIndexes?.additionalFields.includes(Number(index));
      }

      if (fieldName === 'dbOptionAdditionalFields') {
        return !!headerTabIndexes?.dbOptionAdditionalFields.includes(Number(index));
      }

      if (fieldName === HeaderTabFields.countryOfDestinationCode) {
        return licenseTypeNature !== LicenseTypeNature.EPHYTO;
      }

      return fieldName in HeaderTabFields;
    }),
    namesAndParties: differences.some((difference) => {
      const [, fieldName, index] = difference.path.split('/') as [string, string, string];

      if (fieldName === 'additionalFields') {
        return !!namesAndPartiesTabIndexes?.additionalFields.includes(Number(index));
      }

      if (fieldName === 'dbOptionAdditionalFields') {
        return !!namesAndPartiesTabIndexes?.dbOptionAdditionalFields.includes(Number(index));
      }

      return fieldName in NamesAndPartiesTabFields;
    }),
    consignment: differences.some((difference) => {
      const [, fieldName, index] = difference.path.split('/') as [string, string, string];

      if (fieldName === 'additionalFields') {
        return !!consignmentTabIndexes?.additionalFields.includes(Number(index));
      }

      if (fieldName === 'dbOptionAdditionalFields') {
        return !!consignmentTabIndexes?.dbOptionAdditionalFields.includes(Number(index));
      }

      if (fieldName === HeaderTabFields.countryOfDestinationCode) {
        return licenseTypeNature === LicenseTypeNature.EPHYTO;
      }

      return fieldName in ConsignmentTabFields;
    }),
    items: differences.some((difference) => difference.path.startsWith(ArrayFields.items, 1)),
    attachedDocuments: differences.some((difference) => difference.path.startsWith(ArrayFields.attachedDocuments, 1)),
    fees: differences.some((difference) => difference.path.startsWith(ArrayFields.fees, 1)),
  };
}
